import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import FormHelperText from '@mui/material/FormHelperText'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import {
    CSV_MODE,
    SEGMENTS_MODE,
    trainerIdsFromCsv,
    trainerIdsFromText,
} from '../../helpers/trainerNotifications'
import {
    ACCENT,
    BRAND,
    RADIUS,
    SURFACE,
    SYSTEM,
    TEXT,
} from '../../theme/tokens'

// How many of the listed trainer ids are shown before "and N more".
const SHOWN_IDS = 8

/** A chosen file's text (FileReader: `File.text()` is missing from older engines and jsdom). */
const readText = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result ?? ''))
        reader.onerror = () => reject(reader.error)
        reader.readAsText(file)
    })

/** One quick segment: a title, a description and its checkbox, all one clickable label. */
const SegmentCard = ({ title, description, checked, onChange }) => (
    <Box
        component="label"
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1.5,
            borderRadius: `${RADIUS.md}px`,
            cursor: 'pointer',
            border: `1px solid ${checked ? ACCENT.border55 : SURFACE.border}`,
            background: checked ? ACCENT.bg08 : SURFACE.subtle,
            '&:hover': { borderColor: SURFACE.borderHover },
        }}
    >
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
                sx={{
                    typography: 'body2',
                    fontWeight: 600,
                    color: TEXT.primary,
                }}
            >
                {title}
            </Typography>
            <Typography sx={{ typography: 'caption', color: TEXT.muted }}>
                {description}
            </Typography>
        </Box>
        <Checkbox
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            inputProps={{ 'aria-label': title }}
        />
    </Box>
)
SegmentCard.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    checked: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
}

/**
 * Who receives the notification: all trainers or a cohort (Quick Segments),
 * or a list of trainers (CSV Upload). Holds only the paste box and the file
 * note; the audience itself is in `form`, changed through `onChange(patch)`.
 */
const AudienceSegments = ({
    form,
    onChange,
    options,
    totalTrainers,
    errors,
}) => {
    const [pasted, setPasted] = useState('')
    const [fileNote, setFileNote] = useState({ text: '', error: false })

    const setSegment = (key, checked) => {
        const values = {
            grade: 'gradeGroups',
            region: 'regions',
            product: 'productIds',
        }
        onChange({
            allTrainers: false,
            segments: { ...form.segments, [key]: checked },
            // An unticked segment forgets its values.
            ...(checked ? {} : { [values[key]]: [] }),
        })
    }

    const onAll = (checked) =>
        onChange({
            allTrainers: checked,
            ...(checked && {
                segments: { grade: false, region: false, product: false },
                gradeGroups: [],
                regions: [],
                productIds: [],
            }),
        })

    const addIds = (ids) =>
        onChange({ trainerIds: [...new Set([...form.trainerIds, ...ids])] })

    const onFile = async (event) => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (!file) return
        if (!/\.csv$/i.test(file.name)) {
            setFileNote({
                text: `${file.name} is not a .csv file.`,
                error: true,
            })
            return
        }
        try {
            const { ids, invalid, error } = trainerIdsFromCsv(
                await readText(file)
            )
            if (error) {
                setFileNote({ text: `${file.name}: ${error}`, error: true })
                return
            }
            addIds(ids)
            setFileNote({
                text: `${file.name}: ${ids.length} trainer id${
                    ids.length === 1 ? '' : 's'
                } read${
                    invalid.length
                        ? `; ${invalid.length} skipped (not an email or number)`
                        : ''
                }.`,
                error: false,
            })
        } catch {
            setFileNote({
                text: `${file.name} could not be read. Choose it again.`,
                error: true,
            })
        }
    }

    const onPaste = () => {
        const { ids, invalid } = trainerIdsFromText(pasted)
        addIds(ids)
        setPasted('')
        setFileNote({
            text: `${ids.length} id${ids.length === 1 ? '' : 's'} added${
                invalid.length
                    ? `; ${
                          invalid.length
                      } skipped (not an email or number): ${invalid
                          .slice(0, 3)
                          .join(', ')}`
                    : ''
            }.`,
            error: false,
        })
    }

    // detailKey: a second, muted line under each option (and searched too),
    // for lists whose labels alone don't tell the options apart.
    const multi = (key, label, list, valueKey, labelKey, detailKey) => (
        <Autocomplete
            multiple
            size="small"
            options={list ?? []}
            value={(list ?? []).filter((o) => form[key].includes(o[valueKey]))}
            onChange={(_, chosen) =>
                onChange({ [key]: chosen.map((o) => o[valueKey]) })
            }
            getOptionLabel={(o) => o[labelKey] ?? o[valueKey]}
            isOptionEqualToValue={(a, b) => a[valueKey] === b[valueKey]}
            filterOptions={createFilterOptions({
                stringify: (o) =>
                    `${o[labelKey] ?? o[valueKey]} ${
                        detailKey ? o[detailKey] ?? '' : ''
                    }`,
            })}
            renderOption={(props, o) => (
                <li {...props} key={o[valueKey]}>
                    <Box>
                        <Typography sx={{ typography: 'body1' }}>
                            {o[labelKey] ?? o[valueKey]}
                        </Typography>
                        {detailKey && o[detailKey] && (
                            <Typography
                                sx={{
                                    typography: 'caption',
                                    color: TEXT.muted,
                                }}
                            >
                                {o[detailKey]}
                            </Typography>
                        )}
                    </Box>
                </li>
            )}
            filterSelectedOptions
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    required
                    error={Boolean(errors[key])}
                    helperText={errors[key]}
                />
            )}
        />
    )

    const shownIds = form.trainerIds.slice(0, SHOWN_IDS)

    return (
        <Box>
            <Typography
                variant="overline"
                component="div"
                sx={{ color: TEXT.muted, lineHeight: 2, mb: 1 }}
            >
                Audience Segments *
            </Typography>
            <ToggleButtonGroup
                exclusive
                size="small"
                value={form.audienceMode}
                onChange={(_, mode) => mode && onChange({ audienceMode: mode })}
                aria-label="How the audience is chosen"
                sx={{ mb: 2 }}
            >
                <ToggleButton
                    value={SEGMENTS_MODE}
                    sx={{ textTransform: 'none', px: 2 }}
                >
                    Quick Segments
                </ToggleButton>
                <ToggleButton
                    value={CSV_MODE}
                    sx={{ textTransform: 'none', px: 2 }}
                >
                    CSV Upload
                </ToggleButton>
            </ToggleButtonGroup>

            {form.audienceMode === SEGMENTS_MODE ? (
                <>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: 'minmax(0, 1fr)',
                                sm: 'repeat(2, minmax(0, 1fr))',
                            },
                            gap: 1.5,
                        }}
                    >
                        <SegmentCard
                            title="All Trainers"
                            description={`Send to every trainer on the platform${
                                totalTrainers
                                    ? ` · ~${totalTrainers.toLocaleString()} trainers`
                                    : ''
                            }`}
                            checked={form.allTrainers}
                            onChange={onAll}
                        />
                        <SegmentCard
                            title="Grade Group"
                            description="Configure below"
                            checked={form.segments.grade}
                            onChange={(c) => setSegment('grade', c)}
                        />
                        <SegmentCard
                            title="Region"
                            description="Configure below"
                            checked={form.segments.region}
                            onChange={(c) => setSegment('region', c)}
                        />
                        <SegmentCard
                            title="Product Line"
                            description="Configure below"
                            checked={form.segments.product}
                            onChange={(c) => setSegment('product', c)}
                        />
                    </Box>
                    {(form.segments.grade ||
                        form.segments.region ||
                        form.segments.product) && (
                        <Box sx={{ display: 'grid', gap: 2, mt: 2 }}>
                            {form.segments.grade &&
                                multi(
                                    'gradeGroups',
                                    'Grade groups',
                                    options?.grade_groups,
                                    'value',
                                    'label'
                                )}
                            {form.segments.region &&
                                multi(
                                    'regions',
                                    'Regions',
                                    options?.regions,
                                    'value',
                                    'label'
                                )}
                            {form.segments.product &&
                                multi(
                                    'productIds',
                                    'Products',
                                    options?.products,
                                    'product_id',
                                    options?.products?.[0]?.label
                                        ? 'label'
                                        : 'name',
                                    'detail'
                                )}
                            <Typography
                                sx={{
                                    typography: 'caption',
                                    color: TEXT.muted,
                                }}
                            >
                                Trainers must match all selected segments.
                            </Typography>
                        </Box>
                    )}
                </>
            ) : (
                <Box>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1.5,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                        }}
                    >
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<UploadFileOutlinedIcon />}
                            sx={{
                                borderColor: ACCENT.border30,
                                color: ACCENT.text,
                                '&:hover': {
                                    borderColor: BRAND.orange,
                                    background: ACCENT.bg08,
                                },
                            }}
                        >
                            Upload .csv
                            <input
                                hidden
                                type="file"
                                accept=".csv,text/csv"
                                aria-label="Trainer list file"
                                onChange={onFile}
                            />
                        </Button>
                        <Typography
                            sx={{ typography: 'caption', color: TEXT.muted }}
                        >
                            A trainer_id, teacher_id or email column, or a
                            single column of ids.
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            alignItems: 'flex-start',
                            mt: 2,
                        }}
                    >
                        <TextField
                            label="Or paste trainer ids"
                            placeholder="t.18324929@expinfi.com, 18324930"
                            value={pasted}
                            onChange={(e) => setPasted(e.target.value)}
                            multiline
                            minRows={2}
                            size="small"
                            fullWidth
                        />
                        <Button
                            variant="outlined"
                            onClick={onPaste}
                            disabled={!pasted.trim()}
                        >
                            Add
                        </Button>
                    </Box>
                    {fileNote.text && (
                        <Typography
                            role={fileNote.error ? 'alert' : 'status'}
                            sx={{
                                typography: 'caption',
                                mt: 1,
                                color: fileNote.error
                                    ? SYSTEM.errorSoft
                                    : TEXT.secondary,
                            }}
                        >
                            {fileNote.text}
                        </Typography>
                    )}
                    {form.trainerIds.length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 1,
                                }}
                            >
                                <Typography
                                    sx={{
                                        typography: 'body2',
                                        color: TEXT.primary,
                                        fontWeight: 600,
                                    }}
                                >
                                    {form.trainerIds.length} trainer
                                    {form.trainerIds.length === 1
                                        ? ''
                                        : 's'}{' '}
                                    listed
                                </Typography>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        onChange({ trainerIds: [] })
                                        setFileNote({ text: '', error: false })
                                    }}
                                >
                                    Clear all
                                </Button>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                }}
                            >
                                {shownIds.map((id) => (
                                    <Chip
                                        key={id}
                                        label={id}
                                        size="small"
                                        onDelete={() =>
                                            onChange({
                                                trainerIds:
                                                    form.trainerIds.filter(
                                                        (x) => x !== id
                                                    ),
                                            })
                                        }
                                    />
                                ))}
                                {form.trainerIds.length > SHOWN_IDS && (
                                    <Typography
                                        sx={{
                                            typography: 'caption',
                                            color: TEXT.muted,
                                            alignSelf: 'center',
                                        }}
                                    >
                                        and {form.trainerIds.length - SHOWN_IDS}{' '}
                                        more
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
            {errors.audience && (
                <FormHelperText error sx={{ mt: 1 }}>
                    {errors.audience}
                </FormHelperText>
            )}
        </Box>
    )
}

AudienceSegments.propTypes = {
    form: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    // GET /comms/trainer-notifications/audience-options; null while loading.
    options: PropTypes.shape({
        grade_groups: PropTypes.array,
        regions: PropTypes.array,
        products: PropTypes.array,
    }),
    totalTrainers: PropTypes.number,
    errors: PropTypes.object.isRequired,
}

export default AudienceSegments
